// ABOUTME: Dialog for adding or editing a shop configuration.
// ABOUTME: Includes fields for shop identity, database name, and per-day opening hours.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useTranslation } from 'react-i18next';
import { colors } from '../../theme/colors';
import VirtualMembersEditor from './VirtualMembersEditor';

const GOOGLE_MAPS_API_KEY = 'AIzaSyCsgtD1z4L3ayLdl0hRQp7E2Vu78LW51Ms';

function loadGoogleMapsScript() {
  return new Promise((resolve, reject) => {
    if (window.google?.maps?.places) {
      resolve();
      return;
    }
    if (document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')) {
      // Script is loading, wait for it
      const check = setInterval(() => {
        if (window.google?.maps?.places) {
          clearInterval(check);
          resolve();
        }
      }, 100);
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });
}

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const EMPTY_SHOP = {
  shop_id: '',
  name: '',
  db_name: '',
  db_view: '',
  db_connection_prefix: '',
  address: '',
  enabled: true,
  opening_time: '05:00',
  closing_time: '20:00',
  hours: {},
  virtual: false,
  members: [],
  host_shop_id: null,
};

export default function ShopEditDialog({ open, onClose, onSave, onDelete, shop, candidateShops = [], categories = [] }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(EMPTY_SHOP);
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    if (open) {
      if (shop) {
        setFormData({
          shop_id: shop.shop_id || '',
          name: shop.name || '',
          db_name: shop.db_name || '',
          db_view: shop.db_view || '',
          db_connection_prefix: shop.db_connection_prefix || '',
          address: shop.address || '',
          enabled: shop.enabled !== false,
          opening_time: shop.opening_time || '05:00',
          closing_time: shop.closing_time || '20:00',
          hours: shop.hours || {},
          virtual: !!shop.virtual,
          members: shop.members || [],
          host_shop_id: shop.host_shop_id || null,
        });
      } else {
        setFormData({ ...EMPTY_SHOP });
      }
    }
  }, [open, shop]);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    loadGoogleMapsScript().then(() => {
      if (cancelled || !addressInputRef.current) return;
      if (autocompleteRef.current) return;

      const autocomplete = new window.google.maps.places.Autocomplete(
        addressInputRef.current,
        { types: ['address'], componentRestrictions: { country: 'hu' } }
      );
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.formatted_address) {
          setFormData(prev => ({ ...prev, address: place.formatted_address }));
        }
      });
      autocompleteRef.current = autocomplete;
    }).catch(() => {
      // Google Maps failed to load — address field still works as plain text
    });

    return () => {
      cancelled = true;
      autocompleteRef.current = null;
    };
  }, [open]);

  const isNew = !shop;

  const updateField = useCallback((key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  const updateDayHours = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      hours: {
        ...prev.hours,
        [day]: {
          ...(prev.hours[day] || { opening: '05:00', closing: '20:00' }),
          [field]: value,
        },
      },
    }));
  };

  const toggleDayClosed = (day) => {
    setFormData(prev => ({
      ...prev,
      hours: {
        ...prev.hours,
        [day]: prev.hours[day] === null ? { opening: '05:00', closing: '20:00' } : null,
      },
    }));
  };

  const handleSave = () => {
    const { db_name, db_view, db_connection_prefix, members, host_shop_id, ...common } = formData;
    if (formData.virtual) {
      onSave({ ...common, members, host_shop_id });
    } else {
      onSave({ ...common, db_name, db_view, db_connection_prefix, virtual: false });
    }
  };

  const canSave = formData.shop_id && formData.name &&
    (formData.virtual ? formData.members.length > 0 : !!formData.db_name);

  const handleDelete = () => {
    const confirmed = window.confirm(
      t('config.shop.deleteConfirm', { name: formData.name || formData.shop_id })
    );
    if (confirmed) {
      onDelete(formData.shop_id);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '12px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)' }
      }}
    >
      <DialogTitle>
        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: colors.text.primary }}>
          {isNew ? t('config.shop.addShop') : t('config.shop.editShop')}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {/* Shop type - fixed after creation (backend rejects changing it) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontSize: '0.85rem', color: colors.text.secondary }}>
              {t('config.shop.type')}
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={formData.virtual ? 'virtual' : 'normal'}
              disabled={!isNew}
              onChange={(e, value) => {
                if (value !== null) updateField('virtual', value === 'virtual');
              }}
            >
              <ToggleButton value="normal" sx={{ textTransform: 'none', px: 2 }}>
                {t('config.shop.typeNormal')}
              </ToggleButton>
              <ToggleButton value="virtual" sx={{ textTransform: 'none', px: 2 }}>
                {t('config.shop.typeVirtual')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <TextField
            label={t('config.shop.shopId')}
            size="small"
            value={formData.shop_id}
            onChange={(e) => updateField('shop_id', e.target.value)}
            disabled={!isNew}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
          <TextField
            label={t('config.shop.name')}
            size="small"
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
          <TextField
            label={t('config.shop.address')}
            size="small"
            value={formData.address}
            onChange={(e) => updateField('address', e.target.value)}
            inputRef={addressInputRef}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
          {!formData.virtual && (
            <>
              <TextField
                label={t('config.shop.dbName')}
                size="small"
                value={formData.db_name}
                onChange={(e) => updateField('db_name', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
              <TextField
                label={t('config.shop.dbView')}
                size="small"
                value={formData.db_view}
                onChange={(e) => updateField('db_view', e.target.value)}
                placeholder="vw_integ_riport3"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
              <TextField
                label={t('config.shop.dbConnectionPrefix')}
                size="small"
                value={formData.db_connection_prefix}
                onChange={(e) => updateField('db_connection_prefix', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </>
          )}

          {formData.virtual && (
            <VirtualMembersEditor
              candidateShops={candidateShops.filter(candidate => candidate.shop_id !== formData.shop_id)}
              categories={categories}
              members={formData.members}
              hostShopId={formData.host_shop_id}
              onChange={({ members, host_shop_id }) =>
                setFormData(prev => ({ ...prev, members, host_shop_id }))
              }
            />
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch
              checked={formData.enabled}
              onChange={(e) => updateField('enabled', e.target.checked)}
            />
            <Typography sx={{ fontSize: '0.85rem' }}>
              {t('config.shop.enabled')}
            </Typography>
          </Box>

          {/* Opening hours per day */}
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: colors.text.primary, mt: 1 }}>
            {t('config.shop.openingHours')}
          </Typography>
          {DAYS.map(day => {
            const dayHours = formData.hours[day];
            const isClosed = dayHours === null;
            const opening = isClosed ? '' : (dayHours?.opening || formData.opening_time);
            const closing = isClosed ? '' : (dayHours?.closing || formData.closing_time);

            return (
              <Box key={day} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography sx={{ fontSize: '0.8rem', width: 80, color: colors.text.secondary, textTransform: 'capitalize' }}>
                  {t(`config.shop.days.${day}`)}
                </Typography>
                <TextField
                  size="small"
                  value={opening}
                  onChange={(e) => updateDayHours(day, 'opening', e.target.value)}
                  disabled={isClosed}
                  placeholder="HH:MM"
                  sx={{ width: 90, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
                <Typography sx={{ color: colors.text.muted }}>-</Typography>
                <TextField
                  size="small"
                  value={closing}
                  onChange={(e) => updateDayHours(day, 'closing', e.target.value)}
                  disabled={isClosed}
                  placeholder="HH:MM"
                  sx={{ width: 90, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
                <Button
                  size="small"
                  onClick={() => toggleDayClosed(day)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.7rem',
                    color: isClosed ? colors.status.error : colors.text.muted,
                  }}
                >
                  {isClosed ? t('config.shop.closed') : t('config.shop.close')}
                </Button>
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!isNew && (
          <Button
            onClick={handleDelete}
            sx={{ mr: 'auto', textTransform: 'none', color: colors.status.error }}
          >
            {t('config.shop.deleteShop')}
          </Button>
        )}
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {t('config.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave}
          sx={{
            textTransform: 'none',
            bgcolor: colors.ui.button,
            borderRadius: '8px',
            '&:hover': { bgcolor: colors.ui.buttonHover },
          }}
        >
          {t('config.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
